import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useDispatch } from 'react-redux';
import { addAssistant } from '../store/slices/assistantSlice';
import { useFormAutoSave, useFormDraftRestore } from '../hooks/useFormAutoSave';
import ConditionalFields, { commonConditions } from './ConditionalFields';
import AdvancedValidation, { useAdvancedValidation, commonValidators, ValidationRule } from './AdvancedValidation';
import { FiSave, FiClock, FiCheck, FiAlertCircle, FiSettings, FiMic, FiBookOpen, FiTool, FiUser, FiCalendar, FiMessageSquare } from 'react-icons/fi';
import { AssistantConfig, AssistantFormSchema, defaultAssistantFormSchema, FieldDependencyConfig, ToolConfiguration, IntegrationConfig } from '../types/assistant.types';
import { z } from 'zod';
import { theme } from '../styles/theme';

interface AssistantFormProps {
  onClose: () => void;
  onCreated?: (assistant: any) => void;
  initialData?: Partial<AssistantConfig>;
  mode?: 'create' | 'edit';
}

const AssistantForm: React.FC<AssistantFormProps> = ({ 
  onClose, 
  onCreated, 
  initialData = {},
  mode = 'create'
}) => {
  const dispatch = useDispatch();
  
  // Form state
  const [formData, setFormData] = useState({
    name: initialData.name || '',
    description: initialData.description || '',
    model: initialData.model || 'gpt-5',
    systemPrompt: initialData.systemPrompt || 'You are a helpful assistant.',
    temperature: initialData.temperature || 0.7,
    maxTokens: initialData.maxTokens || 1000,
    voiceEnabled: initialData.voiceEnabled || false,
    voiceProvider: initialData.voiceSettings?.provider || 'elevenlabs',
    voiceId: initialData.voiceSettings?.voiceId || '',
    voiceSettings: initialData.voiceSettings || {},
    tools: initialData.tools || [],
    integrations: initialData.integrations || [],
    knowledgeBaseId: initialData.knowledgeBaseId || '',
    calendars: initialData.calendars || [],
    calendarEnabled: (initialData.calendars || []).length > 0
  });

  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'basic' | 'advanced' | 'tools' | 'integrations'>('basic');
  const [showDraftRestore, setShowDraftRestore] = useState(false);
  const [selectedTools, setSelectedTools] = useState<ToolConfiguration[]>(initialData.tools || []);
  const [selectedIntegrations, setSelectedIntegrations] = useState<IntegrationConfig[]>(initialData.integrations || []);
  const [showToolsModal, setShowToolsModal] = useState(false);
  const [showIntegrationsModal, setShowIntegrationsModal] = useState(false);

  // Available tools configuration
  const availableTools: ToolConfiguration[] = [
    {
      id: 'web_search',
      name: 'Web Search',
      description: 'Search the web for current information',
      category: 'web_search',
      enabled: false,
      configuration: { searchEngine: 'google', maxResults: 5 }
    },
    {
      id: 'file_upload',
      name: 'File Upload & Analysis',
      description: 'Upload and analyze documents, images, and files',
      category: 'file_management',
      enabled: false
    },
    {
      id: 'code_execution',
      name: 'Code Execution',
      description: 'Execute Python, JavaScript, and other code',
      category: 'code_execution',
      enabled: false,
      configuration: { allowedLanguages: ['python', 'javascript', 'sql'] }
    },
    {
      id: 'image_generation',
      name: 'Image Generation',
      description: 'Generate images using DALL-E or Stable Diffusion',
      category: 'image_generation',
      enabled: false,
      configuration: { model: 'dall-e-3', size: '1024x1024' }
    },
    {
      id: 'data_analysis',
      name: 'Data Analysis',
      description: 'Analyze CSV, Excel files and create visualizations',
      category: 'data_analysis',
      enabled: false
    },
    {
      id: 'email_sender',
      name: 'Email Sender',
      description: 'Send emails on behalf of the user',
      category: 'communication',
      enabled: false,
      configuration: { provider: 'smtp', fromName: '' }
    },
    {
      id: 'calendar_manager',
      name: 'Calendar Manager',
      description: 'Create, update, and manage calendar events',
      category: 'calendar',
      enabled: false
    },
    {
      id: 'crm_integration',
      name: 'CRM Integration',
      description: 'Manage contacts, leads, and deals',
      category: 'crm',
      enabled: false,
      configuration: { crmType: 'salesforce' }
    },
    {
      id: 'custom_api',
      name: 'Custom API Calls',
      description: 'Make HTTP requests to external APIs',
      category: 'custom_api',
      enabled: false,
      configuration: { baseUrl: '', headers: {} }
    },
    {
      id: 'workflow_automation',
      name: 'Workflow Automation',
      description: 'Trigger automated workflows and processes',
      category: 'workflow_automation',
      enabled: false
    }
  ];

  // Available integrations configuration
  const availableIntegrations: IntegrationConfig[] = [
    {
      id: 'salesforce',
      name: 'Salesforce CRM',
      type: 'crm',
      enabled: false,
      configuration: { instanceUrl: '', apiVersion: 'v58.0' }
    },
    {
      id: 'hubspot',
      name: 'HubSpot CRM',
      type: 'crm',
      enabled: false,
      configuration: { portalId: '' }
    },
    {
      id: 'google_calendar',
      name: 'Google Calendar',
      type: 'calendar',
      enabled: false,
      configuration: { scopes: ['calendar.readonly', 'calendar.events'] }
    },
    {
      id: 'outlook_calendar',
      name: 'Outlook Calendar',
      type: 'calendar',
      enabled: false,
      configuration: { tenantId: '', clientId: '' }
    },
    {
      id: 'slack',
      name: 'Slack',
      type: 'slack',
      enabled: false,
      configuration: { workspaceUrl: '', botToken: '' }
    },
    {
      id: 'microsoft_teams',
      name: 'Microsoft Teams',
      type: 'teams',
      enabled: false,
      configuration: { tenantId: '', appId: '' }
    },
    {
      id: 'notion',
      name: 'Notion',
      type: 'notion',
      enabled: false,
      configuration: { databaseId: '' }
    },
    {
      id: 'airtable',
      name: 'Airtable',
      type: 'airtable',
      enabled: false,
      configuration: { baseId: '', tableName: '' }
    },
    {
      id: 'zapier',
      name: 'Zapier Webhooks',
      type: 'zapier',
      enabled: false,
      configuration: { webhookUrl: '' }
    },
    {
      id: 'custom_webhook',
      name: 'Custom Webhook',
      type: 'webhook',
      enabled: false,
      configuration: { url: '', method: 'POST', headers: {} }
    }
  ];

  // Draft restoration
  const { getDraft, clearDraft } = useFormDraftRestore(`assistant-form-${mode}`);

  // Check for draft on mount
  useEffect(() => {
    const draft = getDraft();
    if (draft && mode === 'create') {
      setShowDraftRestore(true);
    }
  }, [getDraft, mode]);

  // Validation rules
  const validationRules: ValidationRule[] = [
    {
      field: 'name',
      validator: commonValidators.required('Assistant name is required'),
      trigger: 'blur',
      debounceMs: 500
    },
    {
      field: 'name',
      validator: commonValidators.minLength(3, 'Name must be at least 3 characters'),
      trigger: 'change',
      debounceMs: 500
    },
    {
      field: 'name',
      validator: commonValidators.maxLength(100, 'Name must be less than 100 characters'),
      trigger: 'change',
      debounceMs: 500
    },
    {
      field: 'systemPrompt',
      validator: commonValidators.required('System prompt is required'),
      trigger: 'blur'
    },
    {
      field: 'systemPrompt',
      validator: commonValidators.minLength(10, 'System prompt must be at least 10 characters'),
      trigger: 'change',
      debounceMs: 1000
    },
    {
      field: 'temperature',
      validator: commonValidators.range(0, 2, 'Temperature must be between 0 and 2'),
      trigger: 'change'
    },
    {
      field: 'maxTokens',
      validator: commonValidators.range(1, 4000, 'Max tokens must be between 1 and 4000'),
      trigger: 'change'
    }
  ];

  // Use validation hook
  const { validationState, isFormValid, validateAll, hasFieldError, getFieldError } = 
    useAdvancedValidation(validationRules, formData);

  // Auto-save functionality
  const autoSaveConfig = {
    key: `assistant-form-${mode}`,
    data: formData,
    enabled: true,
    debounceMs: 500,
    onSave: async (data: any) => {
      try {
        if (mode === 'edit' && (initialData as any).id) {
          await (api as any).put(`/assistants/${(initialData as any).id}`, data);
        }
      } catch {}
    }
  };

  const { isSaving, isAutoSaving, lastSaved, saveNow, clearDraft: clearAutoSaveDraft, hasUnsavedChanges } = 
    useFormAutoSave(autoSaveConfig);

  // Update form data helper
  const updateFormData = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Tools management
  const toggleTool = (toolId: string) => {
    setSelectedTools(prev => {
      const tool = availableTools.find(t => t.id === toolId);
      if (!tool) return prev;
      
      const exists = prev.find(t => t.id === toolId);
      if (exists) {
        return prev.filter(t => t.id !== toolId);
      } else {
        return [...prev, { ...tool, enabled: true }];
      }
    });
  };

  const updateToolConfiguration = (toolId: string, config: Record<string, any>) => {
    setSelectedTools(prev => prev.map(tool => 
      tool.id === toolId ? { ...tool, configuration: { ...tool.configuration, ...config } } : tool
    ));
  };

  // Integrations management
  const toggleIntegration = (integrationId: string) => {
    setSelectedIntegrations(prev => {
      const integration = availableIntegrations.find(i => i.id === integrationId);
      if (!integration) return prev;
      
      const exists = prev.find(i => i.id === integrationId);
      if (exists) {
        return prev.filter(i => i.id !== integrationId);
      } else {
        return [...prev, { ...integration, enabled: true }];
      }
    });
  };

  const updateIntegrationConfiguration = (integrationId: string, config: Record<string, any>) => {
    setSelectedIntegrations(prev => prev.map(integration => 
      integration.id === integrationId ? { ...integration, configuration: { ...integration.configuration, ...config } } : integration
    ));
  };

  // Handle draft restoration
  const handleRestoreDraft = () => {
    const draft = getDraft();
    if (draft?.data) {
      setFormData(draft.data);
      toast.success('Draft restored');
    }
    setShowDraftRestore(false);
  };

  const handleDiscardDraft = () => {
    clearDraft();
    setShowDraftRestore(false);
  };

  // Form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all fields
    validateAll();
    
    if (!isFormValid) {
      toast.error('Please fix validation errors before submitting');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        model: formData.model,
        temperature: formData.temperature,
        maxTokens: formData.maxTokens,
        systemPrompt: formData.systemPrompt,
        voiceEnabled: formData.voiceEnabled,
        voiceProvider: formData.voiceEnabled ? formData.voiceProvider : undefined,
        voiceId: formData.voiceEnabled ? formData.voiceId : undefined,
        voiceSettings: formData.voiceEnabled ? formData.voiceSettings : undefined,
        tools: selectedTools,
        integrations: selectedIntegrations,
        knowledgeBaseId: formData.knowledgeBaseId || undefined,
        calendars: formData.calendarEnabled ? formData.calendars : []
      };

      const endpoint = mode === 'edit' && initialData.id 
        ? `/assistants/${initialData.id}` 
        : '/assistants';
      
      const method = mode === 'edit' ? 'put' : 'post';
      const res = await (api as any)[method](endpoint, payload);
      
      const assistant = res.data?.assistant || res.data;
      if (assistant) {
        dispatch(addAssistant(assistant));
        toast.success(mode === 'edit' ? 'Assistant updated' : 'Assistant created');
        
        // Clear auto-save draft on successful submission
        if (mode === 'create') {
          clearAutoSaveDraft();
        }
        
        onCreated?.(assistant);
        onClose();
      } else {
        throw new Error('Invalid response');
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 
                     `Failed to ${mode} assistant`;
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  // Schema-driven field rendering helpers
  const [formSchema] = useState<AssistantFormSchema>(defaultAssistantFormSchema);

  const evaluateDependency = (config?: FieldDependencyConfig): boolean => {
    if (!config || !config.rules?.length) return true;
    const results = config.rules.map(rule => {
      const value = String(rule.field).split('.').reduce<any>((acc, key) => (acc ? acc[key] : (formData as any)[key]), (formData as any));
      switch (rule.operator) {
        case 'equals': return value === rule.value;
        case 'notEquals': return value !== rule.value;
        case 'contains': return typeof value === 'string' && value.includes(rule.value);
        case 'greaterThan': return typeof value === 'number' && value > rule.value;
        case 'lessThan': return typeof value === 'number' && value < rule.value;
        case 'isEmpty': return !value || (Array.isArray(value) && value.length === 0) || (typeof value === 'string' && value.trim() === '');
        case 'isNotEmpty': return !!value && (!Array.isArray(value) || value.length > 0) && (typeof value !== 'string' || value.trim() !== '');
        default: return true;
      }
    });
    return config.logic === 'or' ? results.some(Boolean) : results.every(Boolean);
  };

  // Compose Zod schema and pass into validation for composition
  const zodSchema = useMemo(() => z.object({
    name: z.string().min(1, 'Assistant name is required').max(100, 'Name must be less than 100 characters'),
    systemPrompt: z.string().min(10, 'System prompt must be at least 10 characters'),
    temperature: z.number().min(0).max(2),
    maxTokens: z.number().min(1).max(4000)
  }), []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl shadow-2xl"
        style={{ backgroundColor: theme.colors.background.secondary }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: theme.colors.border.main }}>
          <div>
            <h3 className="text-xl font-semibold" style={{ color: theme.colors.text.primary }}>
              {mode === 'edit' ? 'Edit Assistant' : 'Create New Assistant'}
            </h3>
            <div className="flex items-center gap-4 mt-2 text-sm" style={{ color: theme.colors.text.secondary }}>
              {/* Auto-save status */}
              {mode === 'create' && (
                <div className="flex items-center gap-2">
                  {isAutoSaving ? (
                    <>
                      <FiClock className="animate-spin" />
                      <span>Saving draft...</span>
                    </>
                  ) : lastSaved ? (
                    <>
                      <FiCheck className="text-green-500" />
                      <span>Draft saved {new Date(lastSaved).toLocaleTimeString()}</span>
                    </>
                  ) : hasUnsavedChanges ? (
                    <>
                      <FiAlertCircle className="text-yellow-500" />
                      <span>Unsaved changes</span>
                    </>
                  ) : null}
                </div>
              )}
              
              {/* Manual save button */}
              {mode === 'create' && hasUnsavedChanges && (
                <button
                  onClick={saveNow}
                  disabled={isSaving}
                  className="flex items-center gap-1 transition-colors"
                  style={{ color: theme.colors.primary.main }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.color = theme.colors.primary.dark;
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.color = theme.colors.primary.main;
                  }}
                >
                  <FiSave size={14} />
                  Save Draft
                </button>
              )}
            </div>
          </div>
          
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-colors"
            style={{
              color: theme.colors.text.tertiary
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.color = theme.colors.text.secondary;
              e.currentTarget.style.backgroundColor = theme.colors.background.tertiary;
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.color = theme.colors.text.tertiary;
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            ✕
          </button>
        </div>

        {/* Draft Restoration Banner */}
        {showDraftRestore && (
          <div className="bg-blue-50 border-b border-blue-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FiClock className="text-blue-500" />
                <div>
                  <p className="font-medium text-blue-900">Draft Found</p>
                  <p className="text-sm text-blue-700">
                    You have an unsaved draft from a previous session. Would you like to restore it?
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleRestoreDraft}
                  className="px-3 py-1 rounded text-sm transition-colors"
                  style={{
                    backgroundColor: theme.colors.primary.main,
                    color: theme.colors.text.primary
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = theme.colors.primary.dark;
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = theme.colors.primary.main;
                  }}
                >
                  Restore
                </button>
                <button
                  onClick={handleDiscardDraft}
                  className="px-3 py-1 border border-blue-300 text-blue-700 rounded hover:bg-blue-50 text-sm"
                >
                  Discard
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="border-b" style={{ borderColor: theme.colors.border.main }}>
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'basic', name: 'Basic Info', icon: FiSettings },
              { id: 'advanced', name: 'Advanced', icon: FiAlertCircle },
              { id: 'tools', name: 'Tools', icon: FiTool },
              { id: 'integrations', name: 'Integrations', icon: FiBookOpen }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className="flex items-center gap-2 py-4 border-b-2 font-medium text-sm transition-colors"
                style={{
                  borderBottomColor: activeTab === tab.id
                    ? theme.colors.primary.main
                    : 'transparent',
                  color: activeTab === tab.id
                    ? theme.colors.primary.main
                    : theme.colors.text.secondary
                }}
                onMouseOver={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.color = theme.colors.text.primary;
                    e.currentTarget.style.borderBottomColor = theme.colors.border.light;
                  }
                }}
                onMouseOut={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.color = theme.colors.text.secondary;
                    e.currentTarget.style.borderBottomColor = 'transparent';
                  }
                }}
              >
                <tab.icon size={16} />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        <form onSubmit={handleSubmit} className="h-full">
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            <AdvancedValidation
              rules={validationRules}
              formData={formData}
              onValidationChange={(state) => {
                // Handle validation state changes if needed
              }}
              zodSchema={zodSchema}
            />

            {/* Basic Tab (schema-driven) */}
            {activeTab === 'basic' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {formSchema.fields.filter(f => f.section === 'basic').map(field => {
                    if (!evaluateDependency(field.dependsOn)) return null;
                    const key = field.key as string;
                    if (field.type === 'select') {
                      return (
                        <div key={key}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                            {field.label} {field.required ? <span style={{ color: theme.colors.error.main }}>*</span> : null}
                    </label>
                    <select
                            value={(formData as any)[key] || ''}
                            onChange={(e) => updateFormData(key, e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2"
                      style={{
                        backgroundColor: theme.colors.background.tertiary,
                        borderColor: theme.colors.border.main,
                        color: theme.colors.text.primary
                      }}
                    >
                            {(field.options || []).map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                    </select>
                  </div>
                      );
                    }
                    if (field.type === 'textarea') {
                      return (
                        <div key={key} className={key === 'description' ? 'md:col-span-2' : ''}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                            {field.label} {field.required ? <span style={{ color: theme.colors.error.main }}>*</span> : null}
                  </label>
                  <textarea
                            value={(formData as any)[key] || ''}
                            onChange={(e) => updateFormData(key, e.target.value)}
                            rows={key === 'systemPrompt' ? 4 : 3}
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2"
                            style={{
                              backgroundColor: theme.colors.background.tertiary,
                              borderColor: hasFieldError(key) ? theme.colors.error.main : theme.colors.border.main,
                              color: theme.colors.text.primary
                            }}
                            placeholder={key === 'description' ? 'Describe what this assistant does and its purpose...' : "Define the assistant's role, personality, and behavior..."}
                          />
                          {getFieldError(key) && (
                            <p className="mt-1 text-sm" style={{ color: theme.colors.error.main }}>{getFieldError(key)}</p>
                          )}
                </div>
                      );
                    }
                    // default text input
                    return (
                      <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                          {field.label} {field.required ? <span style={{ color: theme.colors.error.main }}>*</span> : null}
                  </label>
                        <input
                          value={(formData as any)[key] || ''}
                          onChange={(e) => updateFormData(key, e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2"
                    style={{
                      backgroundColor: theme.colors.background.tertiary,
                      borderColor: hasFieldError(key) ? theme.colors.error.main : theme.colors.border.main,
                      color: theme.colors.text.primary
                    }}
                          placeholder={field.label}
                  />
                        {getFieldError(key) && (
                          <p className="mt-1 text-sm" style={{ color: theme.colors.error.main }}>{getFieldError(key)}</p>
                  )}
                      </div>
                    );
                  })}
                </div>
                  <p className="mt-1 text-xs text-gray-500">
                    This defines how your assistant behaves and responds to users.
                  </p>
              </div>
            )}

            {/* Advanced Tab */}
            {activeTab === 'advanced' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.text.primary }}>
                      Temperature
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="2"
                      value={formData.temperature}
                      onChange={(e) => updateFormData('temperature', parseFloat(e.target.value))}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2"
                      style={{
                        backgroundColor: theme.colors.background.tertiary,
                        borderColor: hasFieldError('temperature') ? theme.colors.error.main : theme.colors.border.main,
                        color: theme.colors.text.primary
                      }}
                    />
                    <p className="mt-1 text-xs" style={{ color: theme.colors.text.tertiary }}>
                      Controls randomness: 0 = focused, 2 = creative
                    </p>
                    {getFieldError('temperature') && (
                      <p className="mt-1 text-sm" style={{ color: theme.colors.error.main }}>{getFieldError('temperature')}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.text.primary }}>
                      Max Tokens
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="4000"
                      value={formData.maxTokens}
                      onChange={(e) => updateFormData('maxTokens', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2"
                      style={{
                        backgroundColor: theme.colors.background.tertiary,
                        borderColor: hasFieldError('maxTokens') ? theme.colors.error.main : theme.colors.border.main,
                        color: theme.colors.text.primary
                      }}
                    />
                    <p className="mt-1 text-xs" style={{ color: theme.colors.text.tertiary }}>
                      Maximum length of assistant responses
                    </p>
                    {getFieldError('maxTokens') && (
                      <p className="mt-1 text-sm" style={{ color: theme.colors.error.main }}>{getFieldError('maxTokens')}</p>
                    )}
                  </div>
                </div>

                {/* Voice Settings */}
                <div className="border rounded-lg p-4" style={{ borderColor: theme.colors.border.main }}>
                  <div className="flex items-center gap-2 mb-4">
                    <input
                      type="checkbox"
                      id="voiceEnabled"
                      checked={formData.voiceEnabled}
                      onChange={(e) => updateFormData('voiceEnabled', e.target.checked)}
                      className="rounded focus:ring-2"
                      style={{
                        accentColor: theme.colors.primary.main,
                        borderColor: theme.colors.border.main
                      }}
                    />
                    <label htmlFor="voiceEnabled" className="flex items-center gap-2 font-medium" style={{ color: theme.colors.text.primary }}>
                      <FiMic />
                      Enable Voice Capabilities
                    </label>
                  </div>

                  <ConditionalFields condition={commonConditions.voiceEnabled} formData={formData}>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {formSchema.fields
                          .filter(f => f.section === 'advanced' && String(f.key).startsWith('voiceSettings'))
                          .map(field => {
                            const key = field.key as string;
                            if (field.type === 'select') {
                              return (
                                <div key={key}>
                                  <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.text.primary }}>{field.label}</label>
                          <select
                            value={formData.voiceProvider}
                            onChange={(e) => updateFormData('voiceProvider', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2"
                            style={{
                              backgroundColor: theme.colors.background.tertiary,
                              borderColor: theme.colors.border.main,
                              color: theme.colors.text.primary
                            }}
                          >
                                    {(field.options || []).map(opt => (
                                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                          </select>
                        </div>
                              );
                            }
                            return (
                              <div key={key}>
                                <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.text.primary }}>{field.label}</label>
                          <input
                            value={formData.voiceId}
                            onChange={(e) => updateFormData('voiceId', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2"
                            style={{
                              backgroundColor: theme.colors.background.tertiary,
                              borderColor: theme.colors.border.main,
                              color: theme.colors.text.primary
                            }}
                            placeholder="Enter voice ID"
                          />
                        </div>
                            );
                          })}
                      </div>
                    </div>
                  </ConditionalFields>
                </div>
              </div>
            )}

            {/* Tools Tab */}
            {activeTab === 'tools' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold" style={{ color: theme.colors.text.primary }}>Available Tools</h3>
                    <p className="text-sm mt-1" style={{ color: theme.colors.text.secondary }}>
                      Select and configure tools for your assistant
                    </p>
                  </div>
                  <span className="text-sm" style={{ color: theme.colors.text.tertiary }}>
                    {selectedTools.length} tools selected
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {availableTools.map(tool => {
                    const isSelected = selectedTools.some(t => t.id === tool.id);
                    return (
                      <div
                        key={tool.id}
                        className="border rounded-lg p-4 transition-all"
                        style={{
                          borderColor: isSelected ? theme.colors.primary.main : theme.colors.border.main,
                          backgroundColor: isSelected ? theme.colors.primary.main + '10' : theme.colors.background.card
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <FiTool 
                                className="flex-shrink-0" 
                                style={{ color: isSelected ? theme.colors.primary.main : theme.colors.text.tertiary }} 
                                size={16} 
                              />
                              <h4 className="font-medium" style={{ color: theme.colors.text.primary }}>
                                {tool.name}
                              </h4>
                            </div>
                            <p className="text-sm" style={{ color: theme.colors.text.secondary }}>
                              {tool.description}
                            </p>
                            <span 
                              className="inline-block mt-2 px-2 py-1 rounded text-xs"
                              style={{
                                backgroundColor: theme.colors.background.tertiary,
                                color: theme.colors.text.secondary
                              }}
                            >
                              {tool.category.replace('_', ' ')}
                            </span>
                          </div>
                          <label className="flex items-center ml-4">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleTool(tool.id)}
                              className="rounded focus:ring-2"
                              style={{ accentColor: theme.colors.primary.main }}
                            />
                          </label>
                        </div>
                        
                        {isSelected && tool.configuration && (
                          <div className="mt-4 pt-4 border-t" style={{ borderColor: theme.colors.border.light }}>
                            <h5 className="text-sm font-medium mb-2" style={{ color: theme.colors.text.primary }}>Configuration</h5>
                            <div className="space-y-2">
                              {Object.entries(tool.configuration).map(([key, value]) => (
                                <div key={key}>
                                  <label className="block text-xs mb-1" style={{ color: theme.colors.text.secondary }}>
                                    {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                                  </label>
                                  <input
                                    type={typeof value === 'number' ? 'number' : 'text'}
                                    value={String(value)}
                                    onChange={(e) => {
                                      const val = typeof value === 'number' ? Number(e.target.value) : e.target.value;
                                      updateToolConfiguration(tool.id, { [key]: val });
                                    }}
                                    className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1"
                                    style={{
                                      backgroundColor: theme.colors.background.tertiary,
                                      borderColor: theme.colors.border.main,
                                      color: theme.colors.text.primary
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {selectedTools.length === 0 && (
                  <div className="text-center py-8">
                    <FiTool className="mx-auto mb-4" style={{ color: theme.colors.text.tertiary }} size={48} />
                    <p className="mb-2" style={{ color: theme.colors.text.secondary }}>No tools selected</p>
                    <p className="text-sm" style={{ color: theme.colors.text.tertiary }}>
                      Select tools above to enhance your assistant's capabilities
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Integrations Tab */}
            {activeTab === 'integrations' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold" style={{ color: theme.colors.text.primary }}>Integrations & Services</h3>
                    <p className="text-sm mt-1" style={{ color: theme.colors.text.secondary }}>
                      Connect your assistant to external services and platforms
                    </p>
                  </div>
                  <span className="text-sm" style={{ color: theme.colors.text.tertiary }}>
                    {selectedIntegrations.length} integrations active
                  </span>
                </div>

                {/* Knowledge Base */}
                <div className="border rounded-lg p-4" style={{ borderColor: theme.colors.border.main }}>
                  <h4 className="font-medium mb-3 flex items-center gap-2" style={{ color: theme.colors.text.primary }}>
                    <FiBookOpen />
                    Knowledge Base
                  </h4>
                  <select
                    value={formData.knowledgeBaseId}
                    onChange={(e) => updateFormData('knowledgeBaseId', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2"
                    style={{
                      backgroundColor: theme.colors.background.tertiary,
                      borderColor: theme.colors.border.main,
                      color: theme.colors.text.primary
                    }}
                  >
                    <option value="">No knowledge base</option>
                    <option value="kb-company">Company Knowledge Base</option>
                    <option value="kb-product">Product Documentation</option>
                    <option value="kb-support">Support Articles</option>
                    <option value="kb-faq">FAQ Database</option>
                    <option value="kb-custom">Custom Knowledge Base</option>
                  </select>
                </div>

                {/* Available Integrations */}
                <div className="space-y-4">
                  <h4 className="font-medium" style={{ color: theme.colors.text.primary }}>Available Integrations</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {availableIntegrations.map(integration => {
                      const isSelected = selectedIntegrations.some(i => i.id === integration.id);
                      const IconComponent = integration.type === 'crm' ? FiUser : 
                                          integration.type === 'calendar' ? FiCalendar : 
                                          integration.type === 'slack' ? FiMessageSquare :
                                          integration.type === 'webhook' ? FiSettings : FiTool;
                      
                      return (
                        <div
                          key={integration.id}
                          className="border rounded-lg p-4 transition-all"
                          style={{
                            borderColor: isSelected ? theme.colors.primary.main : theme.colors.border.main,
                            backgroundColor: isSelected ? theme.colors.primary.main + '10' : theme.colors.background.card
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <IconComponent 
                                  className="flex-shrink-0" 
                                  style={{ color: isSelected ? theme.colors.primary.main : theme.colors.text.tertiary }} 
                                  size={16} 
                                />
                                <h5 className="font-medium" style={{ color: theme.colors.text.primary }}>
                                  {integration.name}
                                </h5>
                              </div>
                              <span 
                                className="inline-block px-2 py-1 rounded text-xs"
                                style={{
                                  backgroundColor: theme.colors.background.tertiary,
                                  color: theme.colors.text.secondary
                                }}
                              >
                                {integration.type}
                              </span>
                            </div>
                            <label className="flex items-center ml-4">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleIntegration(integration.id)}
                                className="rounded focus:ring-2"
                                style={{ accentColor: theme.colors.primary.main }}
                              />
                            </label>
                          </div>
                          
                          {isSelected && integration.configuration && (
                            <div className="mt-4 pt-4 border-t" style={{ borderColor: theme.colors.border.light }}>
                              <h6 className="text-sm font-medium mb-2" style={{ color: theme.colors.text.primary }}>Configuration</h6>
                              <div className="space-y-2">
                                {Object.entries(integration.configuration).map(([key, value]) => (
                                  <div key={key}>
                                    <label className="block text-xs mb-1" style={{ color: theme.colors.text.secondary }}>
                                      {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                                    </label>
                                    <input
                                      type="text"
                                      value={String(value)}
                                      onChange={(e) => {
                                        updateIntegrationConfiguration(integration.id, { [key]: e.target.value });
                                      }}
                                      className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1"
                                      style={{
                                        backgroundColor: theme.colors.background.tertiary,
                                        borderColor: theme.colors.border.main,
                                        color: theme.colors.text.primary
                                      }}
                                      placeholder={`Enter ${key.toLowerCase()}`}
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Calendar Integration Legacy */}
                <div className="border rounded-lg p-4" style={{ borderColor: theme.colors.border.main }}>
                  <div className="flex items-center gap-2 mb-4">
                    <input
                      type="checkbox"
                      id="calendarEnabled"
                      checked={formData.calendarEnabled}
                      onChange={(e) => updateFormData('calendarEnabled', e.target.checked)}
                      className="rounded focus:ring-2"
                      style={{
                        accentColor: theme.colors.primary.main,
                        borderColor: theme.colors.border.main
                      }}
                    />
                    <label htmlFor="calendarEnabled" className="font-medium" style={{ color: theme.colors.text.primary }}>
                      Enable Legacy Calendar Integration
                    </label>
                  </div>

                  <ConditionalFields condition={commonConditions.calendarEnabled} formData={formData}>
                    <div className="text-sm" style={{ color: theme.colors.text.secondary }}>
                      <p>Legacy calendar integration allows your assistant to:</p>
                      <ul className="list-disc list-inside mt-2 space-y-1">
                        <li>Schedule appointments</li>
                        <li>Check availability</li>
                        <li>Send meeting reminders</li>
                        <li>Manage recurring events</li>
                      </ul>
                    </div>
                  </ConditionalFields>
                </div>
                
                {selectedIntegrations.length === 0 && (
                  <div className="text-center py-8">
                    <FiSettings className="mx-auto mb-4" style={{ color: theme.colors.text.tertiary }} size={48} />
                    <p className="mb-2" style={{ color: theme.colors.text.secondary }}>No integrations configured</p>
                    <p className="text-sm" style={{ color: theme.colors.text.tertiary }}>
                      Connect to external services to expand your assistant's capabilities
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t p-6" style={{ borderColor: theme.colors.border.main, backgroundColor: theme.colors.background.primary }}>
            <div className="flex items-center justify-between">
              <div className="text-sm" style={{ color: theme.colors.text.secondary }}>
                {!isFormValid && (
                  <span className="flex items-center gap-2" style={{ color: theme.colors.error.main }}>
                    <FiAlertCircle />
                    Please fix validation errors before submitting
                  </span>
                )}
              </div>
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors"
                  style={{
                    borderColor: theme.colors.border.main,
                    color: theme.colors.text.primary,
                    backgroundColor: 'transparent'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = theme.colors.background.tertiary;
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !isFormValid}
                  className="px-6 py-2 rounded-lg focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                  style={{
                    backgroundColor: theme.colors.primary.main,
                    color: theme.colors.text.primary
                  }}
                  onMouseOver={(e) => {
                    if (!submitting && isFormValid) {
                      e.currentTarget.style.backgroundColor = theme.colors.primary.dark;
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!submitting && isFormValid) {
                      e.currentTarget.style.backgroundColor = theme.colors.primary.main;
                    }
                  }}
                >
                  {submitting && <FiClock className="animate-spin" />}
                  {submitting 
                    ? (mode === 'edit' ? 'Updating...' : 'Creating...') 
                    : (mode === 'edit' ? 'Update Assistant' : 'Create Assistant')
                  }
                </button>
              </div>
            </div>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default AssistantForm;


